import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { ExecModal, type ExecRow } from "../shared/ExecModal";
import { ToolCallPopup, type ToolCall } from "../shared/ToolCallPopup";
import { ModelSelector, type ModelSelectorValue } from "./ModelSelector";
import { CHANNELS, channelOf, type Channel } from "./ConfigPrompt";
import { DialogPanel } from "../shared/DialogPanel";
import { AlertTriangleIcon, XIcon } from "../shared/Icons";
import { backdropClose } from "../shared/backdrop";

const MAX_CONVERSATIONS = 5;

interface PlaygroundSlot {
  model_provider: string | null;
  model_name: string | null;
  model_label: string | null;
  temperature: number | null;
  reasoning_effort: string | null;
  verbosity: string | null;
  max_tokens: number | null;
}

interface PlaygroundConversation {
  id: string;
  title: string | null;
  model_provider: string | null;
  model_name: string | null;
  temperature: number | null;
  reasoning_effort: string | null;
  verbosity: string | null;
  max_tokens: number | null;
  slots: PlaygroundSlot[] | null;
  eval_user_provider: string | null;
  eval_user_model: string | null;
  eval_user_prompt: string | null;
  eval_max_turns: number | null;
  channel?: Channel;
  created_at: string;
  last_message_at: string | null;
}

interface EvalStep {
  role: "user" | "assistant" | null;
  content: string;
  execution_id: string | null;
  error: string | null;
  done: boolean;
}

const EVAL_END_MARKER = "[FIM]";

const EVAL_PERSONA_BASE = `Você está em um ROLEPLAY: você é um CLIENTE humano conversando com um atendente pelo WhatsApp. Nunca revele que é uma IA e nunca mencione este roleplay.

- Escreva como um cliente real de WhatsApp: mensagens curtas e informais.
- Você pode inventar e simular dados pessoais quando o atendente pedir (nome, telefone, e-mail, cidade etc.) — mantenha-os consistentes até o fim da conversa.
- Defina você mesmo um interesse plausível para este atendimento e mantenha-o.`;

const EVAL_PERSONA_END = `- Quando entender que a conversa chegou ao fim (resolveu o que queria ou não há mais o que tratar), responda apenas: ${EVAL_END_MARKER}`;

const EVAL_PERSONAS: { key: string; label: string; prompt: string }[] = [
  {
    key: "comum",
    label: "Cliente comum",
    prompt: `${EVAL_PERSONA_BASE}
- Perfil: cliente tranquilo e objetivo. Faz uma pergunta por vez, responde o que o atendente pede e colabora com o atendimento.
${EVAL_PERSONA_END}`,
  },
  {
    key: "desafiador",
    label: "Cliente desafiador",
    prompt: `${EVAL_PERSONA_BASE}
- Perfil: cliente apressado e desconfiado. Questiona preços e condições, pede desconto, compara com concorrentes, muda de ideia no meio da conversa e às vezes responde curto ou ignora uma pergunta do atendente. Não é grosseiro, mas testa a paciência.
${EVAL_PERSONA_END}`,
  },
  {
    key: "complexo",
    label: "Cliente complexo",
    prompt: `${EVAL_PERSONA_BASE}
- Perfil: cliente com demanda complexa. Mistura mais de um assunto na mesma conversa (ex.: interesse novo + pendência antiga + dúvida de pagamento), fornece informações incompletas ou fora de ordem, corrige dados que já tinha informado e manda mensagens longas e desorganizadas de vez em quando. Exige que o atendente conduza e organize o atendimento.
${EVAL_PERSONA_END}`,
  },
];

interface PlaygroundMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  tokens_in: number;
  tokens_out: number;
  execution_id: string | null;
  tool_calls: ToolCall[] | null;
  slot_index: number | null;
  error: boolean;
  created_at: string;
}

interface PlaygroundTurn {
  content: string;
  tokens_in: number;
  tokens_out: number;
  execution_id: string | null;
  error: string | null;
  pending_message_ids?: string[] | null;
}

function slotHeader(slot: PlaygroundSlot, agent: any): string {
  const model = slot.model_label ?? slot.model_name ?? agent.model_name;
  const temp = slot.temperature ?? agent.temperature;
  const think = slot.reasoning_effort ?? agent.reasoning_effort ?? "off";
  return `${model} · temp ${temp} · ${think === "off" ? "sem thinking" : `thinking ${think}`}`;
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
}

function fmtUsd(n: number) {
  const maxDecimals = n > 0 && n < 0.01 ? 6 : 2;
  return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: maxDecimals });
}

interface CostSlice { label: string; tokens_in: number; tokens_out: number; tokens_total: number; cost_usd: number }
interface SlotCost { slot_index: number; tokens_in: number; tokens_out: number; cost_usd: number }
interface PlaygroundCost { cost_total_usd: number; cost_breakdown: CostSlice[]; cost_by_slot?: SlotCost[] }

export function TabPlayground({ agent }: { agent: any }) {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [toolPopup, setToolPopup] = useState<ToolCall | null>(null);
  const [execOpen, setExecOpen] = useState<ExecRow | null>(null);
  const [costOpen, setCostOpen] = useState(false);
  const [pendingError, setPendingError] = useState<string | null>(null);
  const [sentText, setSentText] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const { data: conversations = [] } = useQuery<PlaygroundConversation[]>({
    queryKey: ["playground-conversations", agent.id],
    queryFn: () => api.get(`/agents/${agent.id}/playground/conversations`),
  });

  const { data: messages = [], isLoading: loadingMessages } = useQuery<PlaygroundMessage[]>({
    queryKey: ["playground-messages", selectedId],
    queryFn: () => api.get(`/agents/${agent.id}/playground/conversations/${selectedId}/messages`),
    enabled: !!selectedId,
  });

  const { data: cost } = useQuery<PlaygroundCost>({
    queryKey: ["playground-cost", selectedId],
    queryFn: () => api.get(`/agents/${agent.id}/playground/conversations/${selectedId}/cost`),
    enabled: !!selectedId,
  });

  useEffect(() => {
    // rAF: rola depois do paint (bolha otimista/indicador entram fora de `messages`)
    requestAnimationFrame(() => {
      listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
    });
  }, [messages.length, sentText]);

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<PlaygroundConversation>(`/agents/${agent.id}/playground/conversations`, body),
    onSuccess: (conv) => {
      qc.invalidateQueries({ queryKey: ["playground-conversations", agent.id] });
      setSelectedId(conv.id);
      setShowCreate(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/agents/${agent.id}/playground/conversations/${id}`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["playground-conversations", agent.id] });
      if (selectedId === id) setSelectedId(null);
      setConfirmDeleteId(null);
    },
  });

  const sendMutation = useMutation({
    mutationFn: (content: string) =>
      api.post<PlaygroundTurn>(
        `/agents/${agent.id}/playground/conversations/${selectedId}/messages`,
        { content },
        300_000, // turno de LLM + tools pode passar de 15s (multi: espera o slot mais lento)
      ),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["playground-messages", selectedId] });
      qc.invalidateQueries({ queryKey: ["playground-conversations", agent.id] });
      qc.invalidateQueries({ queryKey: ["playground-cost", selectedId] });
      setPendingError(result.error);
      setSentText(null);
      (result.pending_message_ids ?? []).forEach(runSlot);
    },
    onError: () => {
      setPendingError("Falha ao enviar a mensagem. Tente novamente.");
      setSentText(null);
    },
  });

  // Multi chat: cada slot roda num request próprio (retry do placeholder);
  // o balão "aguardando" completa quando o modelo daquele slot responde.
  const [runningIds, setRunningIds] = useState<Set<string>>(new Set());
  function runSlot(msgId: string) {
    setRunningIds((prev) => new Set(prev).add(msgId));
    api.post<PlaygroundTurn>(
      `/agents/${agent.id}/playground/conversations/${selectedId}/messages/${msgId}/retry`,
      {},
      300_000,
    )
      .catch(() => {})
      .finally(() => {
        setRunningIds((prev) => {
          const n = new Set(prev);
          n.delete(msgId);
          return n;
        });
        qc.invalidateQueries({ queryKey: ["playground-messages", selectedId] });
        qc.invalidateQueries({ queryKey: ["playground-cost", selectedId] });
      });
  }

  // Eval: front dirige o loop — um POST /eval/step por vez (fala do simulado OU
  // resposta do agente); para no done, no erro ou no botão Parar.
  const [evalRunning, setEvalRunning] = useState(false);
  const evalStopRef = useRef(false);
  useEffect(() => {
    evalStopRef.current = true; // trocar de conversa interrompe o loop
    setEvalRunning(false);
  }, [selectedId]);

  async function runEval() {
    if (evalRunning) return;
    setEvalRunning(true);
    evalStopRef.current = false;
    setPendingError(null);
    try {
      while (!evalStopRef.current) {
        const r = await api.post<EvalStep>(
          `/agents/${agent.id}/playground/conversations/${selectedId}/eval/step`,
          {},
          300_000,
        );
        qc.invalidateQueries({ queryKey: ["playground-messages", selectedId] });
        qc.invalidateQueries({ queryKey: ["playground-cost", selectedId] });
        qc.invalidateQueries({ queryKey: ["playground-conversations", agent.id] });
        if (r.error) { setPendingError(r.error); break; }
        if (r.done) break;
      }
    } catch {
      setPendingError("Falha ao executar o eval. Tente novamente.");
    }
    setEvalRunning(false);
  }

  async function openExecution(execId: string) {
    const ex = await api.get<ExecRow>(`/agents/${agent.id}/executions/${execId}`);
    setExecOpen(ex);
  }

  function handleSend() {
    const text = draft.trim();
    if (!text || sendMutation.isPending || runningIds.size > 0) return;
    setDraft("");
    setPendingError(null);
    setSentText(text);
    sendMutation.mutate(text);
  }

  if (execOpen) {
    return (
      <ExecModal
        exec={execOpen}
        stepsUrl={`/agents/${agent.id}/executions/${execOpen.id}/steps`}
        onClose={() => setExecOpen(null)}
      />
    );
  }

  const atLimit = conversations.length >= MAX_CONVERSATIONS;
  const selectedConv = conversations.find((c) => c.id === selectedId);
  const slots = selectedConv?.slots ?? null;
  const isEval = selectedConv?.eval_max_turns != null;
  const evalAgentTurns = isEval ? messages.filter((m) => m.role === "assistant").length : 0;
  const evalDone = isEval && (
    evalAgentTurns >= (selectedConv?.eval_max_turns ?? 0)
    || messages.some((m) => m.role === "user" && m.content.includes(EVAL_END_MARKER))
  );

  return (
    <div style={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0, height: "100%" }}>
      {toolPopup && <ToolCallPopup tc={toolPopup} onClose={() => setToolPopup(null)} />}
      {showCreate && (
        <CreateConversationModal
          agent={agent}
          onClose={() => setShowCreate(false)}
          onCreate={(body) => createMutation.mutate(body)}
          isPending={createMutation.isPending}
        />
      )}

      {costOpen && (
        <div {...backdropClose(() => { setCostOpen(false); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <DialogPanel onClose={() => setCostOpen(false)} label="Custo desta conversa" style={{ background: "var(--surface-app)", border: "1px solid var(--line)", borderRadius: 12, padding: 20, width: 480, maxWidth: "92vw", maxHeight: "80vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>Custo desta conversa</span>
              <button aria-label="Fechar" onClick={() => setCostOpen(false)} style={{ background: "none", border: "none", color: "var(--ink-muted)", fontSize: 16, cursor: "pointer" }}><XIcon size={14} /></button>
            </div>
            <div style={{ fontSize: 26, fontWeight: 700, color: "var(--brand)", lineHeight: 1 }}>{fmtUsd(cost?.cost_total_usd ?? 0)}</div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 6, borderBottom: "1px solid var(--line)" }}>
              <span style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em" }}>Modelo</span>
              <div style={{ display: "flex", gap: 16 }}>
                <span style={{ fontSize: 11, color: "var(--ink-muted)", minWidth: 72, textAlign: "right", textTransform: "uppercase", letterSpacing: ".04em" }}>Custo</span>
                <span style={{ fontSize: 11, color: "var(--ink-muted)", minWidth: 60, textAlign: "right", textTransform: "uppercase", letterSpacing: ".04em" }}>Entrada</span>
                <span style={{ fontSize: 11, color: "var(--ink-muted)", minWidth: 60, textAlign: "right", textTransform: "uppercase", letterSpacing: ".04em" }}>Saída</span>
              </div>
            </div>
            {(cost?.cost_breakdown ?? []).length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>Sem consumo registrado nesta conversa.</div>
            ) : (
              (cost?.cost_breakdown ?? []).map((s) => (
                <div key={s.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ fontSize: 13, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {s.label.startsWith("tool:") ? `Ferramenta: ${s.label.slice(5)}` : s.label}
                  </span>
                  <div style={{ display: "flex", gap: 16, flexShrink: 0 }}>
                    <span style={{ fontSize: 13, color: "var(--ink-secondary)", minWidth: 72, textAlign: "right" }}>{fmtUsd(s.cost_usd)}</span>
                    <span style={{ fontSize: 13, color: "var(--ink-secondary)", minWidth: 60, textAlign: "right" }}>{s.tokens_in.toLocaleString()}</span>
                    <span style={{ fontSize: 13, color: "var(--ink-secondary)", minWidth: 60, textAlign: "right" }}>{s.tokens_out.toLocaleString()}</span>
                  </div>
                </div>
              ))
            )}
          </DialogPanel>
        </div>
      )}

      {/* Sidebar */}
      <div style={{ width: 240, borderRight: "1px solid var(--line)", display: "flex", flexDirection: "column", flexShrink: 0, minHeight: 0 }}>
        <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--surface-input)" }}>
          <button
            onClick={() => setShowCreate(true)}
            disabled={atLimit}
            title={atLimit ? `Limite de ${MAX_CONVERSATIONS} conversas atingido — exclua uma para criar outra.` : undefined}
            style={{
              width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid var(--line-strong)",
              background: atLimit ? "var(--surface-hover)" : "var(--brand)", color: atLimit ? "var(--ink-dim)" : "#fff",
              fontSize: 12, fontWeight: 600, cursor: atLimit ? "default" : "pointer",
            }}
          >
            + Nova conversa
          </button>
          <div style={{ fontSize: 11, color: "var(--ink-dim)", marginTop: 6, textAlign: "center" }}>
            {conversations.length}/{MAX_CONVERSATIONS} conversas
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}>
          {conversations.map((c) => (
            <div
              key={c.id}
              onClick={() => setSelectedId(c.id)}
              style={{
                display: "flex", flexDirection: "column", gap: 4, padding: "10px 16px", cursor: "pointer",
                borderBottom: "1px solid var(--surface-hover)",
                background: selectedId === c.id ? "var(--surface-input)" : "transparent",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                <span style={{ fontSize: 13, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.title ?? "Conversa"}
                  {c.channel && c.channel !== "whatsapp" && (
                    <span style={{ fontSize: 11, color: "var(--ink-soft)" }}> · {channelOf(c.channel).label}</span>
                  )}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(c.id); }}
                  style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer", fontSize: 13, flexShrink: 0 }}
                  onMouseOver={(e) => (e.currentTarget.style.color = "var(--danger)")}
                  onMouseOut={(e) => (e.currentTarget.style.color = "var(--ink-muted)")}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    <line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" />
                  </svg>
                </button>
              </div>
              <span style={{ fontSize: 11, color: "var(--ink-dim)" }}>
                {c.eval_max_turns != null
                  ? `Eval · ${c.eval_max_turns} respostas`
                  : c.slots?.length
                    ? `Multi · ${c.slots.length} modelos`
                    : c.model_name ?? `${agent.model_name} (agente)`}
              </span>
              {confirmDeleteId === c.id && (
                <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4, padding: 8, background: "var(--surface-card)", borderRadius: 6, border: "1px solid var(--line)" }}>
                  <span style={{ fontSize: 11, color: "var(--ink-mid)" }}>Excluir esta conversa? Some completamente.</span>
                  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    <button onClick={() => setConfirmDeleteId(null)} style={{ padding: "3px 10px", background: "var(--line)", border: "none", borderRadius: 6, color: "var(--ink-secondary)", fontSize: 11, cursor: "pointer" }}>Cancelar</button>
                    <button onClick={() => deleteMutation.mutate(c.id)} disabled={deleteMutation.isPending} style={{ padding: "3px 10px", background: "var(--danger-soft)", border: "none", borderRadius: 6, color: "var(--danger)", fontSize: 11, cursor: "pointer" }}>Excluir</button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {conversations.length === 0 && (
            <div style={{ padding: 16, fontSize: 12, color: "var(--ink-dim)" }}>Nenhuma conversa ainda.</div>
          )}
        </div>
      </div>

      {/* Chat area */}
      <div style={{ flex: 1, alignSelf: "stretch", display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0, height: "100%" }}>
        <div style={{ padding: "8px 16px", background: "var(--warn-soft)", borderBottom: "1px solid var(--line)", fontSize: 11, color: "var(--warn)", flexShrink: 0 }}>
          <AlertTriangleIcon size={12} /> Tools deste agente rodam de verdade neste ambiente de testes.
        </div>

        {!selectedId ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ink-dim)", fontSize: 13 }}>
            Selecione ou crie uma conversa para testar o agente.
          </div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 16px", borderBottom: "1px solid var(--line)", flexShrink: 0 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {conversations.find((c) => c.id === selectedId)?.title ?? "Conversa"}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
                {slots && (cost?.cost_by_slot ?? []).map((sc) => {
                  const slot = slots[sc.slot_index];
                  if (!slot) return null;
                  const label = slot.model_label ?? slot.model_name;
                  // Mesmo modelo em 2+ slots (temps diferentes): numera pra desambiguar.
                  const dup = slots.filter((s2) => (s2.model_label ?? s2.model_name) === label).length > 1;
                  const temp = slot.temperature ?? agent.temperature;
                  const think = slot.reasoning_effort ?? agent.reasoning_effort ?? "off";
                  return (
                    <span key={sc.slot_index} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, border: "1px solid var(--line)", background: "var(--surface-input)", color: "var(--ink-secondary)" }}>
                      {dup ? `${sc.slot_index + 1}. ` : ""}{label}
                      <span style={{ color: "var(--ink-dim)" }}> · t{temp} · {think}</span>
                      : <strong style={{ color: "var(--brand)" }}>{fmtUsd(sc.cost_usd)}</strong>
                    </span>
                  );
                })}
                <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>
                  {slots ? "Total: " : "Custo: "}<strong style={{ color: "var(--brand)" }}>{fmtUsd(cost?.cost_total_usd ?? 0)}</strong>
                </span>
                <button
                  onClick={() => setCostOpen(true)}
                  style={{ fontSize: 11, padding: "4px 10px", borderRadius: 6, border: "1px solid var(--line-strong)", background: "var(--surface-input)", color: "var(--ink-secondary)", cursor: "pointer" }}
                >
                  Custos
                </button>
              </div>
            </div>
            <div ref={listRef} style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "16px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
              {loadingMessages && <div style={{ color: "var(--ink-dim)", fontSize: 13 }}>Carregando...</div>}
              {!loadingMessages && messages.length === 0 && (
                <div style={{ color: "var(--ink-dim)", fontSize: 13 }}>
                  {isEval ? "Clique em \"Rodar eval\" para iniciar a conversa simulada." : "Envie uma mensagem para começar."}
                </div>
              )}
              {messages.map((msg) => {
                const isAI = msg.role === "assistant";
                const slot = isAI && msg.slot_index != null && slots ? slots[msg.slot_index] : null;
                const isErr = isAI && msg.error;
                const isWaiting = !!slot && !isErr && msg.content === "";
                return (
                  <div key={msg.id} style={{ display: "flex", flexDirection: "column", alignItems: isAI ? "flex-end" : "flex-start", gap: 4 }}>
                    <div style={{
                      maxWidth: "75%",
                      background: isErr ? "var(--danger-soft)" : isAI ? "var(--ok-soft)" : "var(--surface-input)",
                      border: `1px solid ${isErr ? "var(--danger-line)" : isAI ? "var(--ok-line)" : "var(--line-strong)"}`,
                      borderRadius: isAI ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
                      padding: "10px 14px",
                    }}>
                      {slot && (
                        <div style={{ fontSize: 10, fontWeight: 600, color: isErr ? "var(--danger)" : "var(--ok)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>
                          {slotHeader(slot, agent)}
                        </div>
                      )}
                      <div style={{ fontSize: 13, color: isErr ? "var(--danger)" : isWaiting ? "var(--ink-dim)" : isAI ? "var(--ok)" : "var(--ink-strong)", fontStyle: isWaiting ? "italic" : undefined, whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.5, marginBottom: 4 }}>
                        {isErr
                          ? `Erro: ${msg.content}`
                          : isWaiting
                            ? runningIds.has(msg.id) ? "aguardando resposta..." : "sem resposta"
                            : msg.content}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 10, color: "var(--ink-dim)" }}>
                          {fmtDate(msg.created_at)}
                          {isAI && msg.tokens_in > 0 && (
                            <span style={{ marginLeft: 6 }}>↑{msg.tokens_in.toLocaleString()} ↓{msg.tokens_out.toLocaleString()} tk</span>
                          )}
                        </span>
                        {(isErr || (isWaiting && !runningIds.has(msg.id))) && (
                          <button
                            onClick={() => runSlot(msg.id)}
                            style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, border: "1px solid var(--danger-line)", background: "transparent", color: "var(--danger)", cursor: "pointer" }}
                          >
                            Tentar novamente
                          </button>
                        )}
                        {isAI && msg.execution_id && (
                          <button
                            onClick={() => openExecution(msg.execution_id!)}
                            style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, border: "1px solid var(--line-strong)", background: "var(--line)", color: "var(--ink-secondary)", cursor: "pointer" }}
                          >
                            Ver execução
                          </button>
                        )}
                      </div>
                    </div>
                    {isAI && (msg.tool_calls ?? []).length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 4, maxWidth: "75%" }}>
                        {(msg.tool_calls ?? []).map((tc, i) => (
                          <span
                            key={i}
                            onClick={() => setToolPopup(tc)}
                            style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, border: "1px solid var(--brand)", color: "var(--brand)", background: "var(--brand-soft)", cursor: "pointer" }}
                          >
                            {tc.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {sentText && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                  <div style={{
                    maxWidth: "75%", background: "var(--surface-input)", border: "1px solid var(--line-strong)",
                    borderRadius: "12px 12px 12px 4px", padding: "10px 14px",
                  }}>
                    <div style={{ fontSize: 13, color: "var(--ink-strong)", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.5 }}>
                      {sentText}
                    </div>
                  </div>
                </div>
              )}
              {sendMutation.isPending && !slots?.length && (
                <div style={{ alignSelf: "flex-end", fontSize: 12, color: "var(--ink-dim)" }}>digitando...</div>
              )}
              {evalRunning && (
                <div style={{ alignSelf: "center", fontSize: 12, color: "var(--ink-dim)", fontStyle: "italic" }}>eval em execução...</div>
              )}
              {pendingError && (
                <div style={{ alignSelf: "center", fontSize: 12, color: "var(--danger)", background: "var(--danger-soft)", border: "1px solid var(--danger-line)", borderRadius: 8, padding: "6px 12px" }}>
                  Erro: {pendingError}
                </div>
              )}
            </div>

            {isEval ? (
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderTop: "1px solid var(--line)", flexShrink: 0 }}>
                {evalRunning ? (
                  <button
                    onClick={() => { evalStopRef.current = true; }}
                    style={{ padding: "8px 16px", background: "var(--danger-soft)", border: "1px solid var(--danger-line)", borderRadius: 8, color: "var(--danger)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                  >
                    Parar
                  </button>
                ) : (
                  <button
                    onClick={runEval}
                    disabled={evalDone}
                    style={{ padding: "8px 16px", background: evalDone ? "var(--surface-hover)" : "var(--brand)", border: "none", borderRadius: 8, color: evalDone ? "var(--ink-dim)" : "#fff", fontSize: 13, fontWeight: 600, cursor: evalDone ? "default" : "pointer" }}
                  >
                    {evalDone ? "Eval concluído" : messages.length > 0 ? "Continuar eval" : "Rodar eval"}
                  </button>
                )}
                <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>
                  {evalAgentTurns}/{selectedConv?.eval_max_turns} respostas do agente
                  {evalDone && messages.some((m) => m.role === "user" && m.content.includes(EVAL_END_MARKER)) ? " · encerrado pelo cliente simulado" : ""}
                </span>
              </div>
            ) : (
            <div style={{ display: "flex", gap: 8, padding: "12px 16px", borderTop: "1px solid var(--line)", flexShrink: 0 }}>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder="Mensagem de teste..."
                rows={1}
                style={{ flex: 1, resize: "none", padding: "8px 12px", background: "var(--surface-hover)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none", fontFamily: "inherit" }}
              />
              <button
                onClick={handleSend}
                disabled={!draft.trim() || sendMutation.isPending || runningIds.size > 0}
                style={{ padding: "8px 16px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: (!draft.trim() || sendMutation.isPending || runningIds.size > 0) ? 0.5 : 1 }}
              >
                Enviar
              </button>
            </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CreateConversationModal({ agent, onClose, onCreate, isPending }: {
  agent: any;
  onClose: () => void;
  onCreate: (body: Record<string, unknown>) => void;
  isPending: boolean;
}) {
  const [title, setTitle] = useState("");
  const [mode, setMode] = useState<"simples" | "multi" | "eval">("simples");
  const [useOwnModel, setUseOwnModel] = useState(true);
  const [evalPersona, setEvalPersona] = useState("comum");
  const [evalPrompt, setEvalPrompt] = useState(EVAL_PERSONAS[0].prompt);
  const [evalMaxTurns, setEvalMaxTurns] = useState(15);
  const agentModel = (): ModelSelectorValue => ({
    provider: agent.model_provider,
    model_name: agent.model_name,
    temperature: agent.temperature,
    reasoning_effort: agent.reasoning_effort ?? "off",
    verbosity: agent.verbosity ?? "default",
  });
  const [model, setModel] = useState<ModelSelectorValue>(agentModel);
  // Webhook colado: a 1ª execução sai do envelope (texto/mídia/ads), com {{wh}},
  // {{lead}} e número .temp só desta conversa. Nada real é tocado.
  const [webhookText, setWebhookText] = useState("");
  const webhookParsed = (() => { try { return webhookText.trim() ? JSON.parse(webhookText) : null; } catch { return undefined; } })();
  const webhookInvalid = webhookParsed === undefined;
  const [slots, setSlots] = useState<ModelSelectorValue[]>([agentModel(), agentModel()]);
  const [evalModel, setEvalModel] = useState<ModelSelectorValue>(agentModel);
  // Canal testado: prompt e tools daquele canal. Self-Service é só WhatsApp.
  const multiChannel = !agent.template_id && agent.template_kind !== "global";
  const [channel, setChannel] = useState<Channel>("whatsapp");

  function submit() {
    const base = { title: title.trim() || undefined, channel };
    if (mode === "eval") {
      onCreate({
        ...base,
        eval: {
          model_provider: evalModel.provider,
          model_name: evalModel.model_name,
          user_prompt: evalPrompt,
          max_turns: evalMaxTurns,
        },
      });
      return;
    }
    if (mode === "multi") {
      onCreate({
        ...base,
        slots: slots.map((s) => ({
          model_provider: s.provider,
          model_name: s.model_name,
          temperature: s.temperature,
          reasoning_effort: s.reasoning_effort,
          verbosity: s.verbosity === "default" ? null : s.verbosity,
        })),
      });
      return;
    }
    onCreate({
      ...base,
      ...(webhookParsed ? { webhook_payload: webhookParsed } : {}),
      ...(useOwnModel ? {} : {
        model_provider: model.provider,
        model_name: model.model_name,
        temperature: model.temperature,
        reasoning_effort: model.reasoning_effort,
        verbosity: model.verbosity === "default" ? null : model.verbosity,
      }),
    });
  }

  return (
    <div {...backdropClose(() => { onClose(); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
      <DialogPanel onClose={() => onClose()} label="Nova conversa de teste" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: mode === "simples" ? 420 : 520, maxWidth: "94vw", maxHeight: "86vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 16 }}>
        <span style={{ fontWeight: 600, fontSize: 15 }}>Nova conversa de teste</span>

        <div style={{ display: "flex", gap: 8 }}>
          {(["simples", "multi", "eval"] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              style={{
                flex: 1, padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
                border: `1px solid ${mode === m ? "var(--brand)" : "var(--line-strong)"}`,
                background: mode === m ? "var(--brand-soft)" : "var(--surface-input)",
                color: mode === m ? "var(--brand)" : "var(--ink-secondary)",
              }}
            >
              {m === "simples" ? "Conversa simples" : m === "multi" ? "Multi chat" : "Eval"}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 12, color: "var(--ink-secondary)" }}>Nome (opcional)</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex: teste fluxo de agendamento"
            style={{ padding: "8px 12px", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none" }}
          />
        </div>

        {multiChannel && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 12, color: "var(--ink-secondary)" }}>Canal (prompt e tools daquele canal)</label>
            <div role="radiogroup" style={{ display: "flex", gap: 8 }}>
              {CHANNELS.map((c) => (
                <button
                  key={c.id}
                  role="radio"
                  aria-checked={channel === c.id}
                  onClick={() => setChannel(c.id)}
                  style={{
                    flex: 1, padding: "6px 10px", borderRadius: 8, fontSize: 13, cursor: "pointer",
                    border: `1px solid ${channel === c.id ? "var(--brand)" : "var(--line-strong)"}`,
                    background: channel === c.id ? "var(--brand-soft)" : "var(--surface-input)",
                    color: channel === c.id ? "var(--brand)" : "var(--ink-secondary)",
                  }}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {mode === "simples" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
              <input type="radio" checked={useOwnModel} onChange={() => setUseOwnModel(true)} />
              Usar modelo do agente ({agent.model_name})
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
              <input type="radio" checked={!useOwnModel} onChange={() => setUseOwnModel(false)} />
              Escolher outro modelo para este teste
            </label>
          </div>
        )}

        {mode === "simples" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 12, color: "var(--ink-secondary)" }}>Simular a partir de um webhook (opcional)</label>
            <textarea
              value={webhookText}
              onChange={(e) => setWebhookText(e.target.value)}
              placeholder='Cole aqui o JSON do webhook (envelope v2). A 1ª execução roda a partir dele: texto/áudio/imagem transcritos, anúncio resumido, {{wh.*}}, {{lead.*}} e {{contexto.numero}} vêm integrais do webhook. Sessão só do playground — nada é gravado em conversas ou leads reais.'
              spellCheck={false}
              style={{ minHeight: 110, resize: "vertical", padding: "8px 10px", background: "var(--surface-input)", border: `1px solid ${webhookInvalid ? "var(--danger)" : "var(--line)"}`, borderRadius: 8, color: "var(--ink)", fontSize: 12, fontFamily: "var(--font-mono)", outline: "none" }}
            />
            {webhookInvalid && <span style={{ fontSize: 11, color: "var(--danger)" }}>JSON inválido.</span>}
          </div>
        )}
        {mode === "simples" && !useOwnModel && (
          <ModelSelector value={model} onChange={setModel} showMaxTokens={false} showVerbosity compact />
        )}

        {mode === "eval" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>
              Um LLM faz roleplay de cliente conversando com o agente real (prompt + tools). O cliente simulado não sabe que fala com uma IA — e o agente também não.
            </span>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: 12, color: "var(--ink-secondary)" }}>Persona do cliente simulado</label>
              <div style={{ display: "flex", gap: 8 }}>
                {EVAL_PERSONAS.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => { setEvalPersona(p.key); setEvalPrompt(p.prompt); }}
                    style={{
                      flex: 1, padding: "6px 10px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
                      border: `1px solid ${evalPersona === p.key ? "var(--brand)" : "var(--line-strong)"}`,
                      background: evalPersona === p.key ? "var(--brand-soft)" : "var(--surface-input)",
                      color: evalPersona === p.key ? "var(--brand)" : "var(--ink-secondary)",
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: 12, color: "var(--ink-secondary)" }}>Prompt do cliente simulado (editável)</label>
              <textarea
                value={evalPrompt}
                onChange={(e) => { setEvalPrompt(e.target.value); setEvalPersona(""); }}
                rows={9}
                style={{ padding: "8px 12px", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 8, color: "var(--ink)", fontSize: 12, outline: "none", fontFamily: "inherit", resize: "vertical", lineHeight: 1.5 }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: 12, color: "var(--ink-secondary)" }}>Máximo de respostas do agente</label>
              <input
                type="number"
                min={1}
                max={50}
                value={evalMaxTurns}
                onChange={(e) => setEvalMaxTurns(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                style={{ width: 100, padding: "8px 12px", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none" }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: 12, color: "var(--ink-secondary)" }}>Modelo do cliente simulado</label>
              <ModelSelector value={evalModel} onChange={setEvalModel} showMaxTokens={false} compact />
            </div>
          </div>
        )}

        {mode === "multi" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>
              Um input, uma resposta de cada modelo — isoladas entre si. Configuração congelada após a criação.
            </span>
            {slots.map((s, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", gap: 8, padding: 12, background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 8 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-secondary)" }}>Slot {i + 1}</span>
                  {slots.length > 2 && (
                    <button
                      onClick={() => setSlots(slots.filter((_, j) => j !== i))}
                      style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer", fontSize: 12 }}
                    >
                      <XIcon size={12} />
                    </button>
                  )}
                </div>
                <ModelSelector
                  value={s}
                  showVerbosity
                  onChange={(v) => setSlots(slots.map((old, j) => (j === i ? v : old)))}
                  showMaxTokens={false}
                  compact
                />
              </div>
            ))}
            {slots.length < 4 && (
              <button
                onClick={() => setSlots([...slots, agentModel()])}
                style={{ padding: "7px 12px", borderRadius: 8, border: "1px dashed var(--line-strong)", background: "transparent", color: "var(--ink-secondary)", fontSize: 12, cursor: "pointer" }}
              >
                + Adicionar slot ({slots.length}/4)
              </button>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "7px 16px", background: "var(--line)", border: "none", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>Cancelar</button>
          <button onClick={submit} disabled={isPending || (mode === "eval" && !evalPrompt.trim()) || (mode === "simples" && webhookInvalid)} style={{ padding: "7px 16px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: (isPending || (mode === "eval" && !evalPrompt.trim())) ? 0.6 : 1 }}>
            {isPending ? "Criando..." : "Criar"}
          </button>
        </div>
      </DialogPanel>
    </div>
  );
}
